import { LocalDate } from "@js-joda/core";
import { AvatarChip } from "components/designV2/avatarChip/avatarChip";
import { CustomIcon } from "components/icon/customIcon.component";
import { ColorV2 } from "config/colors";
import { GigType } from "model/Gig";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  ScheduleSection,
  ScheduleShiftItem,
  ScheduleWorkerRow,
} from "web-apps/company/components/schedule/types/schedule.types";
import {
  formatDateKey,
  formatItemDateKey,
} from "web-apps/company/components/schedule/utils/schedule.dates";
import { useWorkerAvatar } from "../../hooks/useWorkerAvatar";
import { ShiftBlock } from "../shiftBlock/shiftBlock.component";
import {
  CellHoverAction,
  MonthAvatar,
  MonthAvatarImage,
  MonthInfo,
  MonthInfoSpacer,
  MonthName,
  RowInfo,
  RowSectionMeta,
  RowSectionRole,
  ScheduleRowCell,
  ScheduleRowLayout,
  ScheduleRowContainer,
} from "./scheduleRow.styled";

interface Props {
  workerRow: ScheduleWorkerRow;
  days: LocalDate[];
  shiftBlockSize: "small" | "large" | "pill";
  gridGap?: number;
  variant?: "week" | "month";
  onShiftClick: (payload: ShiftClickPayload) => void;
  onScheduleClick: (payload: ScheduleCreatePayload) => void;
}

const getDateKey = (date: LocalDate): string => formatDateKey(date);

const getItemDateKey = (item: ScheduleShiftItem): string =>
  formatItemDateKey(item.startTime);

export interface ShiftClickPayload {
  item: ScheduleShiftItem;
  worker: ScheduleWorkerRow;
  section: ScheduleSection;
}

export interface ScheduleCreatePayload {
  date: string;
  workerId: number;
  workGroupId: number;
}

const buildItemIndex = (
  items: ScheduleShiftItem[],
): Map<string, ScheduleShiftItem[]> => {
  const index = new Map<string, ScheduleShiftItem[]>();
  items.forEach((item) => {
    const dateKey = getItemDateKey(item);
    const existing = index.get(dateKey);
    if (existing) {
      existing.push(item);
      return;
    }
    index.set(dateKey, [item]);
  });
  return index;
};

const getShiftClickHandler = (
  onShiftClick: Props["onShiftClick"],
  item: ScheduleShiftItem,
  workerRow: ScheduleWorkerRow,
  section: ScheduleSection,
): (() => void) | undefined => {
  if (!onShiftClick) return undefined;
  return () => onShiftClick({ item, worker: workerRow, section });
};

export const ScheduleRow: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const isNonWorker = props.workerRow.isWorker === false;
  const resolvedVariant = props.variant ?? "week";
  const cellJustify = "center";
  const cellOffset = 0;
  const monthAvatarBackground = isNonWorker ? ColorV2.Blue6 : ColorV2.White;
  const monthIconName = isNonWorker ? "v2/shift" : "v2/person";
  const isMonthView = resolvedVariant === "month";
  const canShowHoverAction = props.workerRow.isWorker === true;

  const translatedHeading = isNonWorker
    ? t(`Schedule.${props.workerRow.heading}`, {
        defaultValue: props.workerRow.heading,
      })
    : props.workerRow.heading;

  const translatedText2 = props.workerRow.text2
    ? t(`Schedule.${props.workerRow.text2}`, {
        defaultValue: props.workerRow.text2,
      })
    : undefined;

  const avatarUrl = useWorkerAvatar({
    workerExternalId: props.workerRow.externalId,
    isWorker: props.workerRow.isWorker,
  });

  const dayKeys = useMemo(() => props.days.map(getDateKey), [props.days]);
  const sectionItemIndexes = useMemo(
    () =>
      props.workerRow.sections.map((section) => buildItemIndex(section.items)),
    [props.workerRow.sections],
  );

  return (
    <ScheduleRowContainer>
      {props.workerRow.sections.map((section, sectionIndex) => {
        const itemIndex = sectionItemIndexes[sectionIndex];
        const getItemsForKey = (dateKey: string): ScheduleShiftItem[] =>
          itemIndex.get(dateKey) ?? [];
        const rowCount = Math.max(
          1,
          ...dayKeys.map((dateKey) => getItemsForKey(dateKey).length),
        );
        return Array.from({ length: rowCount }, (_, rowIndex) => (
          <ScheduleRowLayout
            key={`${props.workerRow.id}-${section.id}-${rowIndex}`}
            $dayCount={props.days.length}
            $isFirstRow={sectionIndex === 0 && rowIndex === 0}
          >
            {rowIndex > 0 ? (
              isMonthView ? (
                <MonthInfoSpacer />
              ) : (
                <RowSectionMeta>
                  {props.workerRow.text && (
                    <RowSectionRole>{props.workerRow.text}</RowSectionRole>
                  )}
                </RowSectionMeta>
              )
            ) : sectionIndex === 0 ? (
              <RowInfo>
                {isMonthView ? (
                  <MonthInfo>
                    <MonthAvatar $backgroundColor={monthAvatarBackground}>
                      {avatarUrl ? (
                        <MonthAvatarImage
                          src={avatarUrl}
                          alt={props.workerRow.heading}
                        />
                      ) : (
                        <CustomIcon
                          name={monthIconName}
                          size="18px"
                          color={ColorV2.Blue3}
                        />
                      )}
                    </MonthAvatar>
                    <MonthName title={translatedHeading}>
                      {translatedHeading}
                    </MonthName>
                  </MonthInfo>
                ) : (
                  <AvatarChip
                    title={translatedHeading}
                    subTitle={
                      isNonWorker
                        ? translatedText2
                        : (props.workerRow.text ?? null)
                    }
                    avatarUrl={isNonWorker ? null : avatarUrl}
                    iconName={isNonWorker ? "v2/shift" : undefined}
                    avatarBackgroundColor={
                      isNonWorker ? ColorV2.Blue6 : undefined
                    }
                  />
                )}
              </RowInfo>
            ) : (
              <MonthInfoSpacer />
            )}
            {dayKeys.map((dateKey) => {
              const item = getItemsForKey(dateKey)[rowIndex];
              return (
                <ScheduleRowCell
                  key={`${section.id}-${dateKey}-${rowIndex}`}
                  $justify={cellJustify}
                  $offset={cellOffset}
                >
                  {item &&
                    (() => {
                      const isPublicOrTeamGig =
                        item.gigType === GigType.PUBLIC ||
                        item.gigType === GigType.WORKPOOL ||
                        item.gigType === GigType.WORKGROUP;
                      const workerName =
                        item.workerId != null && item.workerName
                          ? item.workerName
                          : null;
                      const hoverWorkerName =
                        isPublicOrTeamGig && workerName ? workerName : null;
                      return (
                        <ShiftBlock
                          startTime={item.startTime}
                          endTime={item.endTime}
                          status={item.status}
                          size={props.shiftBlockSize}
                          workerName={hoverWorkerName}
                          statusLabelOverrides={undefined}
                          showTypeIcon={
                            !isNonWorker &&
                            Boolean(
                              item.gigType &&
                              item.gigType !== GigType.SOLO &&
                              item.gigType !== GigType.AUTOGENERATED,
                            )
                          }
                          onClick={getShiftClickHandler(
                            props.onShiftClick,
                            item,
                            props.workerRow,
                            section,
                          )}
                        />
                      );
                    })()}
                  {!item && canShowHoverAction && rowIndex === 0 && (
                    <CellHoverAction
                      type="button"
                      $size={props.shiftBlockSize}
                      onClick={(event) => {
                        event.stopPropagation();
                        props.onScheduleClick({
                          date: dateKey,
                          workerId: props.workerRow.id,
                          workGroupId: section.id,
                        });
                      }}
                    >
                      {props.shiftBlockSize === "pill"
                        ? "+"
                        : `+ ${t("Schedule.Schedule")}`}
                    </CellHoverAction>
                  )}
                </ScheduleRowCell>
              );
            })}
          </ScheduleRowLayout>
        ));
      })}
    </ScheduleRowContainer>
  );
};
